import { faPen } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  GridColDef,
  useGridApiRef,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridCellParams,
  GridEventListener,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro'
import { IconButton, Stack, Typography } from '@vapor/material'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { keysOf } from 'src/utils/utils'
import useUpdateReservationResource from '../../../api/update-resource.hook'
import { ReservationF, ReservedResourceF } from '../../../types/reservation-detail.types'
import ResourceCell, { ResourceEditCell } from '../../reserved-resources-tab/reserved-resources-grid/reserved-resource-cells'
import { AdditionalServiceDetailPanelToggle } from '../../reserved-resources-tab/reserved-resources-grid/reserved-resources-expansion-panel-cell'
import { ReservedResourceRow, useReservedResourceRow } from '../../reserved-resources-tab/reserved-resources-grid/reserved-resources-grid.types'
import { useHicCommonDataGridProps, HicDataGridPro } from 'src/app/components/hic-vapor/datagrid/datagrid'
import { ReservedResourceGuestCount, ReservedResourcePhaseCell, ResourceTypeCell, SubmitReservedResourceLayoutIdProps } from '../../reserved-resources-tab/commons/grid-cells'
import ReservedResourceGuestsForm from '../../reserved-resources-tab/guests/guests-form'
import EditTimeRR from '../../reserved-resources-tab/reserved-resources-grid/edit-drawer/edit-time-rr-form'
import { ReservedResourcePhase } from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'
import { compareDates } from 'src/utils/date'
import { useFrontendPhase } from '../../../utils/phase'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import useUpdateReservedResourceHook from 'src/app/hooks/update-reserved-resource/update-reserved-resource.hook'
import { UpdateReservedResourceOutput } from 'src/app/hooks/update-reserved-resource/update-reserved-resource.types'

export type TimeReservedResourcesGridProps = {
  readOnly: boolean
  propertyId: string
  reservation: ReservationF
  reservedResourceId?: string | null
  onSelectedRowsChange: (selectedRows: string[]) => void
  unsavedChanges: (rrId: string | undefined) => void
}

export default function TimeReservedResourcesGrid({ readOnly, propertyId, reservation, reservedResourceId, onSelectedRowsChange, unsavedChanges }: TimeReservedResourcesGridProps) {
  const apiRef = useGridApiRef()
  const { id: tenantId } = useTenant()
  const { hicCommonDataGridProProps } = useHicCommonDataGridProps()
  const [editReservedResource, setEditReservedResource] = useState<ReservedResourceF | null>(null)
  const { updateReservedResourceMutation } = useUpdateReservedResourceHook()

  const columns = useColumns({
    readOnly,
    onEditReservedResourceClick: (row: ReservedResourceRow) => {
      setEditReservedResource(reservation.reservedResources?.find((rr) => rr.id === row.id) ?? null)
    },
    submitReservedResourceLayoutId,
  })
  const { rows, resetRows, setUpdatingResource } = useRows(reservation, reservedResourceId)
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const onSelectionModelChange = useCallback(
    (selectionModel: GridRowSelectionModel): void => {
      const newSelectedRows = selectionModel.map((id) => id.toString())
      setSelectedRows(newSelectedRows)
      onSelectedRowsChange(newSelectedRows)
    },
    [onSelectedRowsChange],
  )
  const isCellEditable = useCallback((params: GridCellParams): boolean => {
    const row = params.row as ReservedResourceRow
    return row.editable
  }, [])

  //#region UPDATE_RESOURCE
  const showSnackBar = useErrorSnackBar()
  const updateResource = useUpdateReservationResource()

  const onResourceChanged = useCallback(
    async (reservedResourceId: string, start: Date, end: Date, resource: { id: string; code: string } | null | undefined, resourceTypeId: string) => {
      const result = await updateResource({ reservation, reservedResourceId, start, end, resourceId: resource?.id || null, resourceTypeId })
      if (result.result === 'ERROR') {
        showSnackBar(result.error)
      }
      resetRows()
    },
    [reservation, setUpdatingResource, resetRows],
  )

  //#endregion UPDATE_RESOURCE

  const onCellEditStop: GridEventListener<'cellEditStop'> = useCallback(
    (params) => {
      const oldRow = rows.find((r) => r.id === params.id)
      const start = oldRow?.currentResourceOccupancy?.start
      const end = oldRow?.currentResourceOccupancy?.end
      const newRow = apiRef.current.getRowWithUpdatedValues(params.id, params.field)
      if (oldRow && newRow && params.field === 'resource' && start && end) {
        const reservedResourceId = oldRow.id
        const oldResourceId = oldRow.resource.resource?.id
        const newResource = newRow.resource.resource
        const newResourceId = newResource?.id
        if (newResourceId !== oldResourceId) {
          onResourceChanged(reservedResourceId, start, end, newResource, newRow.resourceTypeId)
            .then()
            .catch(() => resetRows())
        } else {
          resetRows()
        }
      } else {
        resetRows()
      }
    },
    [rows, onResourceChanged, resetRows],
  )

  //region DETAIL PANEL

  const [expandedDetailSectionIds, setExpandedDetailSectionIds] = useState<GridRowId[]>([])
  useEffect(() => {
    if (rows.length === 1) {
      setExpandedDetailSectionIds(rows.map((r) => r.id))
    } else {
      setExpandedDetailSectionIds([])
    }
    if (rows && (rows.length === 1 || reservedResourceId === null)) {
      const singleReservedResource = reservedResourceId === null && rows.length > 1 ? [] : rows[0] ? [rows[0].id] : []
      setSelectedRows(singleReservedResource)
      onSelectedRowsChange(singleReservedResource)
    }
  }, [rows, onSelectedRowsChange, reservedResourceId])

  //#endregion DETAIL PANEL

  const isRowSelectable = useCallback(
    (params: GridRowParams) => {
      const id = params.id
      if (typeof id === 'string') {
        const phase = reservation.reservedResources?.find((rrr) => rrr.id === id)?.phase
        return phase === ReservedResourcePhase.RESERVATION || phase === ReservedResourcePhase.CHECKIN
      } else {
        return false
      }
    },
    [reservation],
  )

  async function submitReservedResourceLayoutId({ layoutId, reservedResourceId, resourceOccupancyEnd, resourceOccupancyStart }: SubmitReservedResourceLayoutIdProps) {
    const editReservedResource = reservation.reservedResources?.find((r) => r.id === reservedResourceId)
    if (!editReservedResource) return

    return await updateReservedResourceMutation({
      reservedResourceId,
      input: {
        tenantId,
        adults: editReservedResource.adults,
        rateId: editReservedResource.rate.id,
        chargeCategoryId: editReservedResource.chargeCategory.id,
        children: editReservedResource.children,
        reservationId: reservation.id,
        resourceOccupancies: editReservedResource.resourceOccupancies?.map((ro) => {
          if (compareDates(ro.start, resourceOccupancyStart) === 0 && compareDates(ro.end, resourceOccupancyEnd) === 0) {
            return {
              end: ro.end,
              start: ro.start,
              resourceTypeId: ro.reservedResourceType.id,
              assignedResourceTypeId: ro.assignedResourceType.id,
              resourceId: ro.resource?.id,
              reservedResourceLayoutId: layoutId,
            }
          }
          return {
            end: ro.end,
            start: ro.start,
            resourceTypeId: ro.reservedResourceType.id,
            assignedResourceTypeId: ro.assignedResourceType.id,
            resourceId: ro.resource?.id,
            reservedResourceLayoutId: ro.reservedResourceLayoutId,
          }
        }),
      },
    })
  }

  return (
    <>
      <HicDataGridPro<ReservedResourceRow> //
        sx={{
          '& .MuiDataGrid-cell--editable:hover': {
            bgcolor: (theme) => theme.palette.action.hover,
            borderBottomColor: (theme) => `${theme.palette.secondary.main} !important`,
            color: (theme) => theme.palette.secondary.main,
          },
        }}
        {...hicCommonDataGridProProps}
        apiRef={apiRef}
        columns={columns}
        rows={rows}
        getDetailPanelContent={(params) => (
          <ReservedResourceGuestsForm readOnly={readOnly} propertyId={propertyId} reservedResourceId={params.row.id} disableAddGuests={true} unsavedChanges={unsavedChanges} />
        )}
        // getDetailPanelHeight={() => 'auto'}
        // initialState={{
        //   pinnedColumns: {
        //     left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
        //   },
        // }}
        autoHeight
        hideFooter
        checkboxSelection={!readOnly}
        isRowSelectable={isRowSelectable}
        disableRowSelectionOnClick
        // onDetailPanelExpandedRowIdsChange={(ids) => setExpandedDetailSectionIds(ids)}
        //detailPanelExpandedRowIds={expandedDetailSectionIds}
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={onSelectionModelChange}
        isCellEditable={isCellEditable}
        onCellEditStart={() => {
          console.log('edit start...')
        }}
        onCellEditStop={onCellEditStop}
        componentsProps={{
          baseCheckbox: { size: 'small' },
        }}
      />

      {editReservedResource !== null && (
        <EditTimeRR //
          propertyId={propertyId}
          reservedResource={editReservedResource}
          open={editReservedResource !== null}
          onClose={() => setEditReservedResource(null)}
          isOnlyChild={(reservation.reservedResources ?? []).length <= 1}
        />
      )}
    </>
  )
}

function useColumns({
  onEditReservedResourceClick,
  readOnly,
  submitReservedResourceLayoutId,
}: {
  onEditReservedResourceClick: (row: ReservedResourceRow) => void
  readOnly: boolean
  submitReservedResourceLayoutId: (props: SubmitReservedResourceLayoutIdProps) => Promise<UpdateReservedResourceOutput | undefined>
}): GridColDef[] {
  const { t } = useTranslation()
  const { getCurrentPhase } = useFrontendPhase()
  const formatDate = useFormatDate()
  const { formatTableCurrency } = useFormatCurrency()
  const { currency } = useTenant()

  const f = keysOf<ReservedResourceRow>()
  return [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      type: 'string',
      resizable: false,
      flex: 1,
      maxWidth: 40,
      minWidth: 30,
      renderCell: (params) => {
        return <AdditionalServiceDetailPanelToggle id={params.id} value={params.value} />
      },
    },
    {
      field: f('index'),
      headerName: '#',
      resizable: false,
      flex: 1,
      maxWidth: 40,
      minWidth: 30,
      disableColumnMenu: true,
    },
    {
      field: f('resource'),
      headerName: t('pms.booking.reservation.resource_time'),
      flex: 1,
      maxWidth: 120,
      minWidth: 80,
      disableColumnMenu: true,
      editable: true,
      renderCell: (params) => {
        const row = params.row as ReservedResourceRow
        return <ResourceCell {...params} />
      },
      renderEditCell: (params) => {
        const row = params.row as ReservedResourceRow
        return <ResourceEditCell {...params} row={row} />
      },
    },
    {
      field: f('resourceType'),
      headerName: t('pms.booking.reservation.resource_time_type'),
      flex: 1,
      minWidth: 100,
      disableColumnMenu: true,
      renderCell(params) {
        const row = params.row as ReservedResourceRow
        if (row.assignedResourceTypeId !== row.resourceTypeId && row.assignedResourceType) {
          return <ResourceTypeCell requestResourceType={row.resourceType} assignedResourceType={row.assignedResourceType} />
        }
        return params.value
      },
    },
    {
      field: f('phase'),
      headerName: t('pms.booking.reservation.phase'),
      flex: 1,
      minWidth: 100,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row as ReservedResourceRow
        return <ReservedResourcePhaseCell phase={getCurrentPhase(row.start, row.end, row.phase)} />
      },
    },
    {
      field: f('guestCount'),
      headerName: t('pms.booking.reservation.guest_count'),
      maxWidth: 100,
      minWidth: 20,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row as ReservedResourceRow
        return <ReservedResourceGuestCount guestCount={row.guestCount} currentResourceOccupancy={row.currentResourceOccupancy} reservedResourceId={row.id} onSubmit={submitReservedResourceLayoutId} />
      },
    },
    {
      field: f('rate'),
      headerName: t('pms.booking.reservation.rate'),
      flex: 1,
      minWidth: 100,
      disableColumnMenu: true,
    },
    {
      field: f('start'),
      headerName: t('pms.booking.reservation.resource_time.start'),
      maxWidth: 100,
      minWidth: 80,
      resizable: false,
      disableColumnMenu: true,
      valueFormatter: (params) => formatDate(params.value),
      type: 'date',
    },
    {
      field: f('end'),
      headerName: t('pms.booking.reservation.resource_time.end'),
      maxWidth: 100,
      minWidth: 80,
      resizable: false,
      disableColumnMenu: true,
      valueFormatter: (params) => formatDate(params.value),
      type: 'date',
    },
    {
      field: f('time'),
      headerName: t('pms.booking.reservation.time_range'),
      maxWidth: 120,
      minWidth: 100,
      disableColumnMenu: true,
      renderCell(params) {
        const row = params.row as ReservedResourceRow
        if (row.isTimeResource) {
          return (
            <Stack>
              <Typography variant="body">{row.time ? `${formatDate(row.time.from, 'p')} - ${formatDate(row.time.to, 'p')}` : ''}</Typography>
            </Stack>
          )
        } else {
          return <>-</>
        }
      },
    },
    {
      field: f('totalPrice'),
      headerName: t('pms.booking.reservation.production'),
      maxWidth: 120,
      minWidth: 100,
      align: 'right',
      headerAlign: 'right',
      disableColumnMenu: true,
      valueFormatter: (params) => formatTableCurrency(params.value, currency),
    },
    {
      field: 'actions',
      type: 'actions',
      minWidth: 40,
      width: 40,
      resizable: false,
      getActions: (params) => {
        const row = params.row as ReservedResourceRow
        return [
          <IconButton size="small" onClick={() => onEditReservedResourceClick(row)} color="primary" disabled={readOnly}>
            <FontAwesomeIcon icon={faPen} style={{ fontSize: '1rem' }} />
          </IconButton>,
        ]
      },
    },
  ]
}

function useRows(reservation: ReservationF, reservedResourceId?: string | null) {
  const toReservedResourceRow = useReservedResourceRow()
  // const hotelDate = useHotelDate()

  const [rows, setRows] = useState<ReservedResourceRow[]>([])

  const resetRows = useCallback(() => {
    const rrs = reservation.reservedResources || []
    const newRows = rrs
      .filter((frr) => frr.time)
      .map((rr) => {
        const rrr = toReservedResourceRow(rr)
        return {
          editable: rrr.phase !== 'CHECKOUT',
          ...rrr,
        }
      })
      .filter((rr) => !reservedResourceId || rr.id === reservedResourceId)
    setRows(newRows)
  }, [reservation.reservedResources, reservedResourceId])

  useEffect(() => {
    resetRows()
  }, [reservation.reservedResources, reservedResourceId])

  const setUpdatingResource = useCallback(
    (reservedResourceId: string, newResource: { id: string; code: string } | null) => {
      const newRows = rows.map((r) => {
        if (r.id === reservedResourceId) {
          const newR: ReservedResourceRow = {
            ...r,
            resource: {
              type: 'LOADING',
              resource: newResource,
            },
            editable: false,
          }
          return newR
        } else {
          const newR: ReservedResourceRow = {
            ...r,
            editable: false,
          }
          return newR
        }
      })
      setRows(newRows)
    },
    [rows],
  )

  return { rows, setUpdatingResource, resetRows }
}
